// Native (Capacitor) bridge.
//
// Centralises all calls into the @capacitor/* plugins so the rest of the app
// can stay platform-agnostic. Every helper degrades to a safe no-op on the
// web build, where Capacitor.isNativePlatform() returns false.
//
// Wired from App.js on boot: see initNativeBridge({ navigate }).

import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Share } from '@capacitor/share';

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

/** Map an incoming deep-link URL to an in-app react-router path.
 *  Examples:
 *    backyard://event/abc        -> /events/abc
 *    https://backyard.lifestyle/tournaments/123 -> /tournaments/123
 *  (App Links / Universal Links: see public/.well-known/.)
 */
function urlToRoute(url) {
  try {
    const u = new URL(url);
    const path = u.pathname || '/';
    const search = u.search || '';
    // Custom-scheme URLs put the "host" where the first path segment goes.
    if (u.protocol === 'backyard:') {
      const seg = u.host || '';
      const rest = path && path !== '/' ? path : '';
      return `/${seg}${rest}${search}` || '/';
    }
    return `${path}${search}` || '/';
  } catch {
    return null;
  }
}

let _initDone = false;

/** Configure the native shell + wire deep links / hardware back. */
export async function initNativeBridge({ navigate }) {
  if (_initDone || !isNative()) return;
  _initDone = true;

  // Status bar.
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    if (platform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#27306D' });
    }
  } catch (e) {
    console.warn('StatusBar setup failed:', e?.message);
  }

  // Hide splash once React has mounted. With a 300ms fade and matching
  // backgrounds (white system splash → white Capacitor splash → white
  // LogoLoader at t=0), the cold-start hand-off is visually seamless: the
  // user perceives a single continuous boot rather than three layers.
  try {
    await SplashScreen.hide({ fadeOutDuration: 300 });
  } catch (e) {
    console.warn('SplashScreen.hide failed:', e?.message);
  }

  // Hardware back button on Android: prefer history back, exit at root.
  CapApp.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack && window.history.length > 1) {
      window.history.back();
    } else {
      CapApp.exitApp();
    }
  });

  // Deep-link entry while the app is already running.
  CapApp.addListener('appUrlOpen', ({ url }) => {
    const route = urlToRoute(url);
    if (route) navigate(route, { replace: false });
  });

  // Deep-link entry on cold start.
  try {
    const launch = await CapApp.getLaunchUrl();
    if (launch?.url) {
      const route = urlToRoute(launch.url);
      if (route && route !== '/') navigate(route, { replace: true });
    }
  } catch {
    // No launch URL — normal cold start.
  }
}

/** Subtle tap haptic — safe to call from web (no-op). */
export async function tapHaptic() {
  if (!isNative()) return;
  try {
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch {}
}

/** Native share sheet with web Share API fallback. */
export async function nativeShare({ title, text, url }) {
  if (isNative()) {
    try {
      await Share.share({ title, text, url, dialogTitle: title });
      return true;
    } catch {
      return false;
    }
  }
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return true;
    } catch {
      return false;
    }
  }
  return false;
}
